<template>
  <div v-cloak class="v-cloak--invisible">
    <!-- Header -->
    <pdl-section-header
      size="md"
      :is-dividing="true"
      :is-non-stacking="true"
      qaid="sku-grid.bulk-order"
      class="mt-5 xl:mt-0"
    >
      <template v-if="!isPdp" #content>
        <pdl-heading :level="2">{{ $t('appleLabel.bulk') }}</pdl-heading>
      </template>
      <template #actions>
        <div class="text-sm font-medium" qaid="stock-filter-toggle">
          <pdl-toggle-switch
            id="toggle-switch-show-unavailable"
            name="toggle-switch-show-unavailable"
            :model-value="hideUnavailable"
            :text-label="$t('product.stockStatus.filter.inStock')"
            :label-left="true"
            :sync="true"
            @update:modelValue="toggleHideUnavailable"
          />
        </div>
      </template>
    </pdl-section-header>

    <!-- Product Groups wrapper/iterator -->
    <div class="b2b-grid-group">
      <div
        v-for="(productGroup, productGroupIndex) in productGroups"
        :key="productGroupIndex"
        class="b2b-grid-group__section"
        :product-group-index="productGroupIndex"
      >
        <pdl-collapse
          icon-active="indeterminate_check_box"
          icon-inactive="add_box"
          icon-position="left"
          align-icon="start"
          :model-value="viewBreakpointMoreThan('medium') ? [productGroupIndex] : []"
          qaid="bulk-product-collapse"
        >
          <bulk-product-grid
            :hide-unavailable="hideUnavailable"
            :image-size="{width: 192, height: 144}"
            :base-product="baseProductCode"
            :product-group="productGroup"
            :product-type="productGroup.productType"
            :product-group-index="productGroupIndex"
            :is-pdp="isPdp"
            :product-groups="productGroups"
            @add-to-cart="handleAddToCart"
            @update-stock-watch-state="(payload) => $emit('update-stock-watch-state', payload)"
            @update-cart-or-list-column="updateColumns"
          />
        </pdl-collapse>
      </div>
    </div>

    <pdp-archived-product-link v-if="isHybridActiveProduct && b2b" />

    <bundling-dialog
      :show-dialog="isBundleDialogVisible"
      :add-to-cart-button-caption="$t('text.addToCart')"
      @cancel="setBundleDialogVisibility({type: 'GRID', isVisible: false})"
      @add-to-cart="setBundleDialogVisibility({type: 'GRID', isVisible: false})"
    />
  </div>
</template>

<script>
import {mapMutations, mapState, mapGetters} from 'vuex';
import {PdlCollapse} from '@pedal/pdl-collapse';
import {PdlToggleSwitch} from '@pedal/pdl-toggle-switch';
import BulkProductGrid from '@/components/containers/bulk-product/BulkProductGrid';
import BundlingDialog from '@/components/containers/pdp/BundlingDialog';
import PdpArchivedProductLink from '@/components/containers/pdp/PdpArchivedProductLink';
import {PdlSectionHeader, PdlHeading} from '@pedal/pdl-section-header';

export default {
  components: {
    PdlCollapse,
    PdlToggleSwitch,
    BulkProductGrid,
    BundlingDialog,
    PdlSectionHeader,
    PdlHeading,
    PdpArchivedProductLink,
  },

  props: {
    productGroups: {
      type: Array,
      default: () => [],
    },
    isPdp: Boolean,
    productStatus: {
      type: Object,
      default: () => null,
    },
  },

  data() {
    return {
      hideUnavailable: false,
    };
  },

  computed: {
    baseProductCode() {
      return !this.productGroups || !('code' in this.productGroups) ? '' : this.productGroups.code;
    },

    isBundleDialogVisible: {
      get() {
        return this.isGridBundleDialogVisible;
      },

      set(isVisible) {
        this.setBundleDialogVisibility({type: 'GRID', isVisible});
      },
    },

    isHybridActiveProduct() {
      return (
        this.productStatus?.baseProductArchivedStatus === 'HYBRID' &&
        (this.productStatus?.archivedStatus == 'HYBRID' || this.productStatus?.archivedStatus == 'ACTIVE') &&
        window.location.href.includes('/p/')
      );
    },

    ...mapState('skuGrid', ['baseProductsSkus']),
    ...mapState('productDetails', ['isGridBundleDialogVisible']),
    ...mapState('backend', ['b2b']),
    ...mapState('productBundle', ['bundleTemplateIds']),
    ...mapState('viewSize', ['viewBreakpoint']),
    ...mapGetters('viewSize', ['viewBreakpointMoreThan']),
  },

  methods: {
    updateColumns(responseData) {
      const responseEntries = responseData.response;
      const colName = responseData.colName;
      this.productGroups.forEach((productGroup) => {
        const products = productGroup.products;
        this.$store.dispatch('skuGrid/updateCartOrListColumn', {products, responseEntries, colName}).then((res) => {
          productGroup.products = res;
        });
      });
    },

    handleAddToCart() {
      let bundleIds = [];

      //   Get the respective bundletemplateId, if present, open up the bundle modal.
      this.baseProductsSkus.forEach((code) => {
        let id = this.bundleTemplateIds.find((templateId) => templateId.includes(code));
        id && bundleIds.push(id);
        this.setActiveIds(bundleIds);
      });

      if (bundleIds.length > 0) this.setBundleDialogVisibility({type: 'GRID', isVisible: true});
    },

    toggleHideUnavailable(val) {
      this.hideUnavailable = val;
    },

    ...mapMutations('productBundle', ['setActiveIds']),
    ...mapMutations('productDetails', ['setBundleDialogVisibility']),
  },
};
</script>
